import React from 'react';
import { motion } from 'framer-motion';
import Container from '../layout/Container';
import Button from './Button';

interface CTASectionProps {
  title: string;
  subtitle?: string;
  primaryButtonText: string;
  secondaryButtonText?: string;
  primaryButtonTo: string;
  secondaryButtonTo?: string;
  background?: 'light' | 'dark' | 'gradient';
}

const CTASection: React.FC<CTASectionProps> = ({
  title,
  subtitle,
  primaryButtonText,
  secondaryButtonText,
  primaryButtonTo,
  secondaryButtonTo,
  background = 'light',
}) => {
  const backgroundClasses = {
    light: 'bg-neutral-50',
    dark: 'bg-neutral-900 text-white',
    gradient: 'bg-gradient-to-r from-primary-600 to-primary-800 text-white',
  };

  return (
    <section className={`py-16 md:py-24 ${backgroundClasses[background]}`}>
      <Container>
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            className="text-3xl md:text-4xl font-bold font-serif mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            {title}
          </motion.h2>
          {subtitle && (
            <motion.p
              className="text-lg md:text-xl mb-8 opacity-90"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              {subtitle}
            </motion.p>
          )}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Button
              variant={background === 'light' ? 'primary' : 'accent'}
              size="lg"
              to={primaryButtonTo}
            >
              {primaryButtonText}
            </Button>
            {secondaryButtonText && secondaryButtonTo && (
              <Button
                variant={background === 'light' ? 'outline' : 'ghost'}
                size="lg"
                to={secondaryButtonTo}
              >
                {secondaryButtonText}
              </Button>
            )}
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default CTASection;